/**
 * Widgets Page - Testing ground for RefineVoice components
 */

import { useEffect, useRef, useState } from 'react';
import { TranscriptionService } from '../services/transcription.service';

// Import web component - this registers it in the browser
// The predev script ensures this package is built before dev starts
import '@refinevoice/recorder/pure';

export default function WidgetsPage() {
  const [activeTab, setActiveTab] = useState<
    'pure' | 'react' | 'textarea' | 'textarea-react' | 'textarea-smart' | 'editor'
  >('pure');

  // Handle URL hash navigation
  useEffect(() => {
    const hash = window.location.hash.slice(1) as typeof activeTab;
    if (
      hash &&
      ['pure', 'react', 'textarea', 'textarea-react', 'textarea-smart', 'editor'].includes(hash)
    ) {
      setActiveTab(hash);
    }

    const handleHashChange = () => {
      const newHash = window.location.hash.slice(1) as typeof activeTab;
      if (
        newHash &&
        ['pure', 'react', 'textarea', 'textarea-react', 'textarea-smart', 'editor'].includes(
          newHash
        )
      ) {
        setActiveTab(newHash);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const navigateToTab = (tab: typeof activeTab) => {
    window.location.hash = tab;
    setActiveTab(tab);
  };

  return (
    <div className="widgets-page">
      <header className="widgets-header">
        <h1>RefineVoice Components</h1>
        <p>Test our progressive voice recording components - all from one package</p>
      </header>

      <nav className="widgets-nav">
        <button
          type="button"
          className={`nav-button ${activeTab === 'pure' ? 'active' : ''}`}
          onClick={() => navigateToTab('pure')}
        >
          Pure Recorder
        </button>
        <button
          type="button"
          className={`nav-button ${activeTab === 'react' ? 'active' : ''}`}
          onClick={() => navigateToTab('react')}
        >
          React Recorder
        </button>
        <button
          type="button"
          className={`nav-button ${activeTab === 'textarea' ? 'active' : ''}`}
          onClick={() => navigateToTab('textarea')}
        >
          Textarea (JS)
        </button>
        <button
          type="button"
          className={`nav-button ${activeTab === 'textarea-react' ? 'active' : ''}`}
          onClick={() => navigateToTab('textarea-react')}
        >
          Textarea (React)
        </button>
        <button
          type="button"
          className={`nav-button ${activeTab === 'textarea-smart' ? 'active' : ''}`}
          onClick={() => navigateToTab('textarea-smart')}
        >
          Textarea Smart
        </button>
        <button
          type="button"
          className={`nav-button ${activeTab === 'editor' ? 'active' : ''}`}
          onClick={() => navigateToTab('editor')}
        >
          Full Editor
        </button>
      </nav>

      <main className="widgets-content">
        {activeTab === 'pure' && <PureRecorderDemo />}
        {activeTab === 'react' && <ReactRecorderDemo />}
        {activeTab === 'textarea' && <TextareaDemo />}
        {activeTab === 'textarea-react' && <ReactTextareaDemo />}
        {activeTab === 'textarea-smart' && <TextareaSmartDemo />}
        {activeTab === 'editor' && <EditorDemo />}
      </main>

      <style>{`
        .widgets-page {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem;
          font-family: system-ui, -apple-system, sans-serif;
        }

        .widgets-header {
          text-align: center;
          margin-bottom: 3rem;
        }

        .widgets-header h1 {
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          color: #1a1a1a;
        }

        .widgets-header p {
          font-size: 1.1rem;
          color: #666;
        }

        .widgets-nav {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
          border-bottom: 2px solid #e5e5e5;
        }

        .nav-button {
          padding: 1rem 2rem;
          background: none;
          border: none;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          font-size: 1rem;
          font-weight: 500;
          color: #666;
          transition: all 0.2s;
        }

        .nav-button:hover:not(:disabled) {
          color: #333;
          background: #f5f5f5;
        }

        .nav-button.active {
          color: #2563eb;
          border-bottom-color: #2563eb;
        }

        .nav-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .widgets-content {
          background: white;
          border-radius: 8px;
          padding: 2rem;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .coming-soon {
          text-align: center;
          padding: 4rem 2rem;
          color: #666;
          font-size: 1.1rem;
        }
      `}</style>
    </div>
  );
}

function PureRecorderDemo() {
  const [transcript, setTranscript] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [componentReady, setComponentReady] = useState(false);
  const recorderElementRef = useRef<any>(null);

  useEffect(() => {
    // The import at the top of the file registers the component
    // Wait a tick for registration to complete
    setTimeout(() => {
      if (!customElements.get('refinevoice-recorder')) {
        setComponentReady(false);
        return;
      }

      setComponentReady(true);

      // Create web component dynamically
      const element = document.createElement('refinevoice-recorder') as any;
      element.setAttribute('language', 'en');
      element.style.display = 'none';

      const handleTranscript = (event: any) => {
        const data = event.detail;
        console.log('Transcript data:', data);

        if (data.isFinal) {
          setTranscript((prev) => (prev ? `${prev} ${data.transcription}` : data.transcription));
        }
      };

      const handleRecordingState = (event: any) => {
        setIsRecording(event.detail);
        setError(null);
      };

      const handleError = (event: any) => {
        setError(event.detail);
        setIsRecording(false);
      };

      element.addEventListener('transcript', handleTranscript);
      element.addEventListener('recordingstate', handleRecordingState);
      element.addEventListener('error', handleError);

      document.body.appendChild(element);
      recorderElementRef.current = element;
    }, 100);

    return () => {
      if (recorderElementRef.current?.parentNode) {
        recorderElementRef.current.parentNode.removeChild(recorderElementRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      // Get Deepgram token
      const transcriptionService = new TranscriptionService();
      const { token } = await transcriptionService.getToken();

      // Set API key on the recorder element
      if (recorderElementRef.current) {
        recorderElementRef.current.setAttribute('api-key', token);
        await recorderElementRef.current.startRecording();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start recording');
    }
  };

  const stopRecording = () => {
    recorderElementRef.current?.stopRecording();
  };

  const clearTranscript = () => {
    setTranscript('');
    setError(null);
  };

  return (
    <div className="recorder-demo">
      <div className="demo-header">
        <h2>Pure Recording Engine</h2>
        <p>No UI dependencies - just audio recording and transcription</p>
        <div className="bundle-size">Bundle: 5.9KB (gzipped)</div>
      </div>

      <div className="demo-content">
        {!componentReady ? (
          <div className="coming-soon">
            <h3>🚧 Component Not Loaded</h3>
            <p>
              The RefineVoiceRecorder web component will be available once the recorder package is
              built.
            </p>
            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#64748b' }}>
              Run: <code>npm run build --workspace=packages/recorder</code>
            </p>
          </div>
        ) : (
          <>
            {/* Custom UI controls */}
            <div className="controls">
              <button
                type="button"
                className={`record-button ${isRecording ? 'recording' : ''}`}
                onClick={isRecording ? stopRecording : startRecording}
              >
                {isRecording ? '⏹️ Stop Recording' : '🎤 Start Recording'}
              </button>

              <button
                type="button"
                className="clear-button"
                onClick={clearTranscript}
                disabled={!transcript}
              >
                🗑️ Clear
              </button>
            </div>

            {/* Status display */}
            <div className="status">
              {isRecording && <div className="recording-status">🔴 Recording... Speak now!</div>}

              {error && <div className="error-status">❌ Error: {error}</div>}
            </div>

            {/* Transcript display */}
            <div className="transcript-container">
              <h3>Transcript:</h3>
              <div className="transcript">
                {transcript || 'No transcript yet. Click "Start Recording" to begin...'}
              </div>
            </div>
          </>
        )}

        {/* Code examples */}
        <div className="code-example">
          <h3>📦 Minimal Example:</h3>
          <pre>{`import '@refinevoice/recorder/pure';

// Create element
const recorder = document.createElement('refinevoice-recorder');
document.body.appendChild(recorder);

// Listen for transcripts
recorder.addEventListener('transcript', (e) => {
  if (e.detail.isFinal) {
    console.log('Final:', e.detail.transcription);
  }
});

// Start recording
await recorder.startRecording();`}</pre>
        </div>

        <div className="code-example">
          <h3>🚀 Maximal Example (Full Control):</h3>
          <pre>{`import '@refinevoice/recorder/pure';

const recorder = document.createElement('refinevoice-recorder');
recorder.setAttribute('language', 'en-US');
recorder.setAttribute('include-alternatives', 'true');
recorder.setAttribute('include-confidence', 'true');
document.body.appendChild(recorder);

// Handle interim transcripts
recorder.addEventListener('transcript', (e) => {
  const { transcription, isFinal, confidence, alternatives, words } = e.detail;

  if (!isFinal) {
    // Show interim transcript (gray text)
    updateInterimDisplay(transcription);
  } else {
    // Process final transcript
    appendFinalTranscript(transcription);

    // Access word-level data
    words?.forEach(word => {
      console.log(\`"\${word.word}" (confidence: \${word.confidence})\`);
    });

    // Show alternatives
    if (alternatives?.length > 0) {
      console.log('Alternatives:', alternatives);
    }
  }
});

// Handle recording state
recorder.addEventListener('recordingstate', (e) => {
  const isRecording = e.detail;
  updateUIState(isRecording);
});

// Handle errors
recorder.addEventListener('error', (e) => {
  console.error('Error:', e.detail);
  showErrorMessage(e.detail);
});

// Control recording
await recorder.startRecording();
// ... later ...
recorder.stopRecording();

// Check state
if (recorder.isRecording) {
  console.log('Currently recording');
}

// Cleanup
recorder.remove();`}</pre>
        </div>
      </div>

      <style>{`
        .recorder-demo {
          max-width: 800px;
        }

        .demo-header {
          margin-bottom: 2rem;
        }

        .demo-header h2 {
          font-size: 1.8rem;
          margin-bottom: 0.5rem;
          color: #1a1a1a;
        }

        .demo-header p {
          color: #666;
        }

        .controls {
          display: flex;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .record-button {
          padding: 1rem 2rem;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          background: #2563eb;
          color: white;
        }

        .record-button:hover:not(:disabled) {
          background: #1d4ed8;
        }

        .record-button.recording {
          background: #dc2626;
          animation: pulse 1.5s infinite;
        }

        .record-button.recording:hover {
          background: #b91c1c;
        }

        .record-button:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .clear-button {
          padding: 1rem 2rem;
          border: 2px solid #e5e5e5;
          border-radius: 8px;
          background: white;
          color: #666;
          cursor: pointer;
          transition: all 0.2s;
        }

        .clear-button:hover:not(:disabled) {
          border-color: #d1d5db;
          color: #333;
        }

        .clear-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .status {
          margin-bottom: 2rem;
        }

        .recording-status {
          padding: 1rem;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          color: #dc2626;
          font-weight: 500;
        }

        .error-status {
          padding: 1rem;
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 6px;
          color: #dc2626;
        }

        .warning-status {
          padding: 1rem;
          background: #fffbeb;
          border: 1px solid #fed7aa;
          border-radius: 6px;
          color: #d97706;
        }

        .transcript-container {
          margin-bottom: 2rem;
        }

        .transcript-container h3 {
          margin-bottom: 1rem;
          color: #1a1a1a;
        }

        .transcript {
          min-height: 120px;
          padding: 1rem;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          color: #333;
          line-height: 1.6;
          white-space: pre-wrap;
        }

        .code-example {
          margin-top: 2rem;
        }

        .code-example h3 {
          margin-bottom: 1rem;
          color: #1a1a1a;
        }

        .code-example pre {
          background: #1e293b;
          color: #e2e8f0;
          padding: 1.5rem;
          border-radius: 8px;
          overflow-x: auto;
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .demo-section {
          max-width: 900px;
        }

        .demo-header {
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 2px solid #e5e5e5;
        }

        .demo-header h2 {
          font-size: 1.8rem;
          margin-bottom: 0.5rem;
          color: #1a1a1a;
        }

        .demo-header p {
          color: #666;
          margin-bottom: 0.5rem;
        }

        .bundle-size {
          display: inline-block;
          padding: 0.25rem 0.75rem;
          background: #f0f9ff;
          color: #0369a1;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .coming-soon {
          text-align: center;
          padding: 3rem 2rem;
          background: #f8fafc;
          border: 2px dashed #cbd5e1;
          border-radius: 8px;
          margin-bottom: 2rem;
        }

        .coming-soon h3 {
          font-size: 1.5rem;
          color: #64748b;
          margin-bottom: 0.5rem;
        }

        .coming-soon p {
          color: #94a3b8;
        }

        .features {
          margin-top: 2rem;
        }

        .features h3 {
          margin-bottom: 1rem;
          color: #1a1a1a;
        }

        .features ul {
          list-style: none;
          padding: 0;
        }

        .features li {
          padding: 0.5rem 0;
          color: #333;
        }

        .demo-textarea {
          resize: vertical;
          min-height: 150px;
        }

        .live-demo {
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: #f8fafc;
          border-radius: 8px;
        }

        .live-demo h3 {
          margin-bottom: 1rem;
          color: #1a1a1a;
        }

        .use-case {
          margin-top: 2rem;
          padding: 1.5rem;
          background: #f0f9ff;
          border-radius: 8px;
        }

        .use-case h3 {
          color: #0369a1;
          margin-bottom: 1rem;
        }

        .use-case ul {
          list-style: none;
          padding: 0;
        }

        .use-case li {
          padding: 0.5rem 0;
          color: #334155;
        }

        .info-status {
          padding: 1rem;
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          border-radius: 6px;
          color: #1e40af;
          margin-top: 1rem;
        }
      `}</style>
    </div>
  );
}

function ReactRecorderDemo() {
  return (
    <div className="demo-section">
      <div className="demo-header">
        <h2>React Recorder Wrapper</h2>
        <p>React component wrapper for the pure recording engine</p>
        <div className="bundle-size">Bundle: ~5KB (gzipped)</div>
      </div>

      <div className="demo-content">
        <div className="coming-soon">
          <h3>🚧 Coming Soon</h3>
          <p>React wrapper component with hooks and state management</p>
        </div>

        <div className="code-example">
          <h3>📦 Minimal Example:</h3>
          <pre>{`import { ReactRecorder } from '@refinevoice/recorder/react';
import { useState } from 'react';

function App() {
  const [text, setText] = useState('');

  return (
    <div>
      <ReactRecorder
        onTranscript={(data) => {
          if (data.isFinal) {
            setText(prev => prev + ' ' + data.transcription);
          }
        }}
      />
      <p>{text}</p>
    </div>
  );
}`}</pre>
        </div>

        <div className="code-example">
          <h3>🚀 Maximal Example (Full Control):</h3>
          <pre>{`import { ReactRecorder } from '@refinevoice/recorder/react';
import { useState, useRef } from 'react';

function App() {
  const recorderRef = useRef(null);
  const [transcript, setTranscript] = useState('');
  const [interim, setInterim] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState(null);

  return (
    <div>
      <ReactRecorder
        ref={recorderRef}
        language="en-US"
        includeAlternatives={true}
        includeConfidence={true}
        onTranscript={(data) => {
          if (!data.isFinal) {
            setInterim(data.transcription);
          } else {
            setTranscript(prev => prev + ' ' + data.transcription);
            setInterim('');

            // Access word-level details
            data.words?.forEach(word => {
              console.log(\`Word: \${word.word}, Confidence: \${word.confidence}\`);
            });
          }
        }}
        onRecordingStateChange={setIsRecording}
        onError={setError}
      />

      <button onClick={() => recorderRef.current?.startRecording()}>
        {isRecording ? 'Stop' : 'Start'}
      </button>

      <div>
        <strong>Final:</strong> {transcript}
        {interim && <em style={{ color: '#999' }}>{interim}</em>}
      </div>

      {error && <div className="error">{error}</div>}
    </div>
  );
}`}</pre>
        </div>

        <div className="features">
          <h3>Features:</h3>
          <ul>
            <li>✅ React hooks integration</li>
            <li>✅ TypeScript support</li>
            <li>✅ Ref-based control</li>
            <li>✅ Event callbacks</li>
            <li>✅ Built on pure engine (5.9KB)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function TextareaDemo() {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const instanceRef = useRef<any>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!textareaRef.current) return;

    const initializeTextarea = async () => {
      try {
        // Dynamic import to handle package not being built yet
        // @ts-expect-error - Package may not be built yet, handled gracefully at runtime
        const { RefineVoiceTextarea } = await import('@refinevoice/recorder/textarea');

        // Get Deepgram token
        const transcriptionService = new TranscriptionService();
        const { token } = await transcriptionService.getToken();

        // Initialize textarea recorder
        instanceRef.current = new RefineVoiceTextarea({
          textarea: textareaRef.current!,
          // Empty string = same origin; endpoint constants already include '/api'
          backendUrl: '',
          widgetKey: token,
          language: 'en',
          showRecordButton: true,
          enableRefinement: false,
          onRecordingStateChange: (recording: boolean) => {
            setIsRecording(recording);
            setError(null);
          },
          onError: (err: string) => {
            setError(err);
            setIsRecording(false);
          },
        });

        setIsInitialized(true);
        setError(null);
      } catch (err) {
        console.error('Failed to initialize textarea recorder:', err);
        setError('Package not built yet. Run: npm run build --workspace=packages/recorder');
        setIsInitialized(false);
      }
    };

    initializeTextarea();

    return () => {
      if (instanceRef.current?.destroy) {
        instanceRef.current.destroy();
      }
    };
  }, []);

  return (
    <div className="demo-section">
      <div className="demo-header">
        <h2>Textarea Integration (Vanilla JS)</h2>
        <p>Connect recording engine to any textarea element</p>
        <div className="bundle-size">Bundle: ~8KB (gzipped)</div>
      </div>

      <div className="demo-content">
        {/* Live Demo Section */}
        <div className="live-demo">
          <h3>Try It Out:</h3>
          <p>
            Click the record button to start voice input. Transcript appears at cursor position.
          </p>

          <textarea
            ref={textareaRef}
            className="demo-textarea"
            placeholder="Click the record button to start voice input..."
            rows={8}
            style={{
              width: '100%',
              padding: '1rem',
              fontSize: '1rem',
              border: '2px solid #e5e5e5',
              borderRadius: '8px',
              fontFamily: 'inherit',
              marginBottom: '0.5rem',
            }}
          />

          {/* Status indicators */}
          {isRecording && (
            <div className="recording-status" style={{ color: '#dc2626', marginBottom: '0.5rem' }}>
              🔴 Recording...
            </div>
          )}

          {error && (
            <div className="error-status" style={{ color: '#dc2626', marginBottom: '0.5rem' }}>
              ⚠️ {error}
            </div>
          )}

          {!isInitialized && !error && (
            <div className="info-status" style={{ color: '#64748b', marginBottom: '0.5rem' }}>
              ⏳ Initializing...
            </div>
          )}
        </div>

        {/* Code Examples */}
        <div className="code-example">
          <h3>📦 Minimal Example:</h3>
          <pre>{`import { RefineVoiceTextarea } from '@refinevoice/recorder/textarea';

// Attach to existing textarea - record only
const recorder = new RefineVoiceTextarea({
  selector: '#my-textarea',
  backendUrl: 'https://refinevoice.com',
  widgetKey: 'your-widget-key',
  language: 'en',
  showRecordButton: true,
  enableRefinement: false
});

// That's it! Button added, ready to record.
// Transcripts auto-insert at cursor position.`}</pre>
        </div>

        <div className="code-example">
          <h3>🚀 Maximal Example:</h3>
          <pre>{`import { RefineVoiceTextarea } from '@refinevoice/recorder/textarea';

const instance = new RefineVoiceTextarea({
  // Target
  textarea: document.querySelector('#my-textarea'),

  // Authentication
  backendUrl: 'https://refinevoice.com',
  widgetKey: 'your-widget-key',

  // Recording options
  language: 'en-US',
  showRecordButton: true,
  enableRefinement: false,

  // Event handlers
  onRecordingStateChange: (isRecording) => {
    console.log('Recording:', isRecording);
    updateUI(isRecording);
  },

  onTranscript: (data) => {
    console.log('Transcript:', data.transcription);
    if (data.isFinal) {
      trackAnalytics('transcript_received');
    }
  },

  onError: (error) => {
    console.error('Error:', error);
    showNotification(error);
  }
});

// Programmatic control
await instance.startRecording();
instance.stopRecording();

// Cleanup
instance.destroy();`}</pre>
        </div>

        {/* Features */}
        <div className="features">
          <h3>Features:</h3>
          <ul>
            <li>✅ Framework agnostic (vanilla JS)</li>
            <li>✅ Cursor position management</li>
            <li>✅ Text insertion at cursor</li>
            <li>✅ Selected text replacement</li>
            <li>✅ Auto-focus management</li>
            <li>✅ Live transcription</li>
            <li>✅ Error handling</li>
            <li>✅ Small bundle size (~8KB)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function ReactTextareaDemo() {
  return (
    <div className="demo-section">
      <div className="demo-header">
        <h2>Textarea Integration (React)</h2>
        <p>React component for textarea with voice input</p>
        <div className="bundle-size">Bundle: ~10KB (gzipped)</div>
      </div>

      <div className="demo-content">
        <div className="coming-soon">
          <h3>🚧 Coming Soon</h3>
          <p>React component with textarea integration</p>
        </div>

        <div className="code-example">
          <h3>Usage Example:</h3>
          <pre>{`import { ReactTextarea } from '@refinevoice/recorder/textarea-react';

function MyForm() {
  const [value, setValue] = useState('');

  return (
    <ReactTextarea
      value={value}
      onChange={setValue}
      apiKey="your-api-key"
      placeholder="Type or click to speak..."
      showRecordButton={true}
      language="en"
    />
  );
}`}</pre>
        </div>

        <div className="features">
          <h3>Features:</h3>
          <ul>
            <li>✅ Controlled component</li>
            <li>✅ Custom record button</li>
            <li>✅ Cursor management</li>
            <li>✅ Text selection handling</li>
            <li>✅ React state integration</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function TextareaSmartDemo() {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const instanceRef = useRef<any>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [instanceReady, setInstanceReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [transcript, setTranscript] = useState('');

  useEffect(() => {
    let mounted = true;

    const initializeTextarea = async () => {
      if (!textareaRef.current) return;

      try {
        setIsLoading(true);
        setError(null);

        // Fetch Deepgram token
        const transcriptionService = new TranscriptionService();
        const { token } = await transcriptionService.getToken();

        if (!mounted) return;

        // Dynamically import RefineVoiceTextarea
        // @ts-expect-error - Package may not be built yet, handled gracefully at runtime
        const { RefineVoiceTextarea } = await import('@refinevoice/recorder/textarea-smart');

        if (!mounted || !textareaRef.current) return;

        // Create instance
        instanceRef.current = new RefineVoiceTextarea({
          textarea: textareaRef.current,
          // Empty string = same origin; endpoint constants already include '/api'
          backendUrl: '',
          widgetKey: token,
          language: 'en',
          showRecordButton: true,
          enableRefinement: true,
          refinementPrompts: {
            quick: 'Fix grammar and improve clarity',
            formal: 'Make this professional and formal',
            casual: 'Make this friendly and casual',
            summarize: 'Create a brief summary',
            expand: 'Add more detail',
            simplify: 'Simplify for general audience',
          },
          onTranscript: (data: { transcription: string; isFinal: boolean }) => {
            if (data.isFinal) {
              setTranscript(data.transcription);
            }
          },
          onRefined: (text: string) => {
            setIsRefining(false);
            console.log('Text refined:', text);
          },
          onError: (err: Error | string) => {
            setError(err instanceof Error ? err.message : err);
            setIsRecording(false);
            setIsRefining(false);
          },
        });

        // Monitor recording state via the recorder element
        const recorder = instanceRef.current.recorder;
        if (recorder) {
          recorder.addEventListener('recordingstate', (event: any) => {
            setIsRecording(event.detail);
          });
        }

        setInstanceReady(true);
        setIsLoading(false);
      } catch (err) {
        if (!mounted) return;
        console.error('Failed to initialize TextareaSmartDemo:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize');
        setIsLoading(false);
        setInstanceReady(false);
      }
    };

    initializeTextarea();

    return () => {
      mounted = false;
      if (instanceRef.current) {
        instanceRef.current.destroy();
        instanceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="demo-section">
      <div className="demo-header">
        <h2>Textarea Smart - Recording + Refinement</h2>
        <p>Voice input with AI-powered text refinement</p>
        <div className="bundle-size">Bundle: ~12KB (gzipped)</div>
      </div>

      <div className="demo-content">
        {/* Live Demo Section */}
        <div className="live-demo">
          <h3>Try It Out:</h3>
          <p>Click in the textarea, then use the controls to record or refine your text.</p>

          {/* Status indicators */}
          {isLoading && (
            <div className="info-status" style={{ marginBottom: '1rem' }}>
              ⏳ Loading voice recording interface...
            </div>
          )}

          {error && (
            <div className="error-status" style={{ marginBottom: '1rem', color: '#dc2626' }}>
              ❌ Error: {error}
            </div>
          )}

          {instanceReady && !isLoading && !error && (
            <div style={{ marginBottom: '1rem' }}>
              <div
                className="success-status"
                style={{
                  padding: '0.75rem',
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '6px',
                  marginBottom: '0.5rem',
                }}
              >
                ✅ Ready! Recording and refinement controls appear above the textarea.
                {isRecording && (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      background: '#fee2e2',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                    }}
                  >
                    🎙️ Recording in progress...
                  </div>
                )}
                {isRefining && (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      padding: '0.5rem',
                      background: '#e0e7ff',
                      borderRadius: '4px',
                      fontWeight: 'bold',
                    }}
                  >
                    ✨ Refining text with AI...
                  </div>
                )}
              </div>
              {transcript && (
                <div
                  style={{
                    padding: '0.5rem',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '0.875rem',
                    color: '#6b7280',
                  }}
                >
                  Last transcript: "{transcript}"
                </div>
              )}
            </div>
          )}

          <textarea
            ref={textareaRef}
            className="demo-textarea"
            placeholder="Click here and try recording or typing some text to refine..."
            rows={10}
            style={{
              width: '100%',
              padding: '1rem',
              fontSize: '1rem',
              border: '2px solid #e5e5e5',
              borderRadius: '8px',
              fontFamily: 'inherit',
              marginBottom: '1rem',
            }}
          />
        </div>

        {/* Code Examples */}
        <div className="code-example">
          <h3>📦 Minimal Example (Record Only):</h3>
          <pre>{`import { RefineVoiceTextarea } from '@refinevoice/recorder/textarea-smart';

// Attach to existing textarea - record only
const recorder = new RefineVoiceTextarea({
  selector: '#my-textarea',
  backendUrl: 'https://refinevoice.com',
  widgetKey: 'wk_free_xxxxx',
  showRecordButton: true
});

// That's it! Button added, ready to record.
// Transcripts auto-insert at cursor position.`}</pre>
        </div>

        <div className="code-example">
          <h3>🚀 Maximal Example (Record + AI Refinement):</h3>
          <pre>{`import { RefineVoiceTextarea } from '@refinevoice/recorder/textarea-smart';

const instance = new RefineVoiceTextarea({
  // Target
  selector: '#my-textarea',

  // Authentication
  backendUrl: 'https://refinevoice.com',
  widgetKey: 'wk_paid_xxxxx',

  // Recording options
  language: 'en-US',
  showRecordButton: true,

  // Refinement options
  enableRefinement: true,
  refinementPrompts: {
    quick: 'Fix grammar and improve clarity',
    formal: 'Make this professional and formal',
    casual: 'Make this friendly and casual',
    summarize: 'Create a concise summary',
    expand: 'Add more detail and examples',
    simplify: 'Simplify for general audience'
  },

  // Event handlers
  onTranscript: (data) => {
    console.log('Transcript:', data.transcription);
    if (data.confidence < 0.8) {
      console.warn('Low confidence transcript');
    }
  },

  onRefined: (text) => {
    console.log('Refined text:', text);
    trackAnalytics('text_refined');
  },

  onError: (error) => {
    console.error('Error:', error);
    showUserNotification(error);
  }
});

// Programmatic control
await instance.startRecording();
instance.stopRecording();

// Refinement options
await instance.refineSelection();  // Refine selected text
await instance.refineAll();       // Refine entire textarea

// WordPress integration example
document.querySelector('.refine-btn').onclick = async () => {
  const selection = window.getSelection().toString();
  if (selection) {
    await instance.refineSelection();
  } else {
    await instance.refineAll();
  }
};

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
  instance.destroy();
});`}</pre>
        </div>

        {/* Features */}
        <div className="features">
          <h3>Features:</h3>
          <ul>
            <li>✅ Record at cursor position</li>
            <li>✅ AI-powered text refinement</li>
            <li>✅ Multiple refinement prompts</li>
            <li>✅ Refine selection or entire text</li>
            <li>✅ Token-based authentication</li>
            <li>✅ Works with any textarea</li>
            <li>✅ Framework agnostic (vanilla JS)</li>
            <li>✅ Small bundle size (~12KB)</li>
          </ul>
        </div>

        {/* WordPress Use Case */}
        <div className="use-case">
          <h3>Perfect for WordPress:</h3>
          <ul>
            <li>📝 Blog post writing (Classic Editor)</li>
            <li>📄 Page content (Gutenberg blocks)</li>
            <li>💬 Comment forms</li>
            <li>📋 Custom fields (ACF)</li>
            <li>📧 Contact forms</li>
            <li>⚙️ Admin settings</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

function EditorDemo() {
  return (
    <div className="demo-section">
      <div className="demo-header">
        <h2>Full Rich Editor</h2>
        <p>Complete rich text editor with voice input and alternatives</p>
        <div className="bundle-size">Bundle: ~25KB (gzipped)</div>
      </div>

      <div className="demo-content">
        <div className="coming-soon">
          <h3>🚧 Coming Soon</h3>
          <p>Full-featured editor with TipTap integration</p>
        </div>

        <div className="code-example">
          <h3>Usage Example:</h3>
          <pre>{`import { RefineVoiceEditor } from '@refinevoice/recorder/editor';

function DocumentEditor() {
  const [content, setContent] = useState('');

  return (
    <RefineVoiceEditor
      value={content}
      onChange={setContent}
      apiKey="your-api-key"
      language="en"
      showAlternatives={true}
      showWordConfidence={true}
      prompts={{
        refine: "Make this more professional",
        summarize: "Create a summary",
        expand: "Add more detail"
      }}
    />
  );
}`}</pre>
        </div>

        <div className="features">
          <h3>Features:</h3>
          <ul>
            <li>✅ TipTap rich text editor</li>
            <li>✅ Word-level alternatives</li>
            <li>✅ Confidence highlighting</li>
            <li>✅ Text refinement tools</li>
            <li>✅ Custom prompts</li>
            <li>✅ Keyboard shortcuts</li>
            <li>✅ Full accessibility</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
